// components/Regola.tsx — blueprint C7, redesign spec §4.8.
//
// The inline "Regola d'oro" / principle callout: a BORDERLESS, soft
// gradient-tinted moment that lives INSIDE Il Prof's prose (prof-chat, lesson,
// rivedi), not a chunky bordered card. Per spec §4.8:
//   "Soft gradient-tinted background, no box border, no inset margins — a
//    typographic moment inside Il Prof's prose, not a chunky card."
//
// Structure mirrors the prototype `.regola` (docs/specs/v28-prototype/kit.css
// §5 + screens/prof-chat.html, lesson.html, rivedi.html):
//   .regola      → soft gradient tint fill, no border, radius-sm, sp-3/sp-4 pad
//   .regola .label → small uppercase BRAND-INK eyebrow "Regola d'oro"
//   .regola .line  → italic body line (the rule itself)
//
// LIGHT-ONLY (V28 Phase F1): tokens.mode is hard-pinned to 'light'. The tint is
// still authored mode-aware (matching the CitationChip convention, the other
// gradient-tint chat moment) so every token key stays live and the persisted
// shape is unchanged, but it resolves to the light tint in practice.
//
// Static by design: the prototype `.regola` carries no entrance animation — it's
// a quiet typographic moment, so there is nothing to gate for reduced motion.

import type { JSX, ReactNode } from 'react';
import { useTokens } from '../state/theme';

export interface RegolaProps {
  /** The rule itself — rendered as the italic body line. */
  children: ReactNode;
  /** Override the eyebrow label. Defaults to the Italian "Regola d'oro". */
  label?: string;
}

/**
 * Inline borderless "Regola d'oro" callout (blueprint C7 / spec §4.8).
 * Drop it inside Il Prof's prose to surface the golden rule of a principle.
 */
export function Regola({ children, label = 'Regola d’oro' }: RegolaProps): JSX.Element {
  const t = useTokens();

  // Soft gradient tint — the prototype's --brand-grad-soft. Mode-aware to keep
  // parity with the other chat gradient-tint moment (CitationChip); resolves to
  // the light tint under the F1 light-only lockdown.
  const tintBg = t.mode === 'dark'
    ? 'linear-gradient(135deg, rgba(124,90,255,0.16), rgba(255,90,216,0.14))'
    : 'linear-gradient(135deg, rgba(105,67,255,0.09), rgba(229,71,196,0.09))';

  return (
    <div
      role="note"
      aria-label={label}
      style={{
        // BORDERLESS + NO INSET MARGINS (spec §4.8). Padding only.
        border: 0,
        margin: 0,
        padding: '12px 16px',                 // prototype sp-3 / sp-4
        background: tintBg,
        borderRadius: t.radius.sm,            // prototype --r-card-sm
        position: 'relative',
      }}
    >
      <span
        style={{
          display: 'block',
          fontFamily: t.fonts.body,
          fontSize: 10.5,                      // prototype 0.65rem
          fontWeight: 800,
          letterSpacing: 0.9,                  // prototype 0.09em
          textTransform: 'uppercase',
          color: t.accentInk,                  // brand-ink
          marginBottom: 4,
        }}
      >
        {label}
      </span>
      <span
        style={{
          display: 'block',
          fontFamily: t.fonts.body,
          fontStyle: 'italic',
          fontSize: 15,                        // prototype 0.95rem
          lineHeight: 1.4,
          color: t.text,
        }}
      >
        {children}
      </span>
    </div>
  );
}
